import { useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
	ExchangeDeviceCodeResponse,
	requestDeviceCode,
	migrateDevice,
} from '../../utils/pairing';
import AppStateContext from '../../AppStateProvider/context';
import { ScreenEnum } from '../../enums';
import data from '../../data.json';
// @ts-ignore
import { debugLogResponse } from '../PairDevice';
import * as packageJson from '../../../package.json';

async function pollForToken(
	tokenUrl: string,
	device_code: string,
	intervalSeconds = 5,
	maxSeconds = 90
) {
	const formData = new FormData();
	formData.append('device_code', device_code);
	formData.append(
		'grant_type',
		'urn:ietf:params:oauth:grant-type:device_code'
	);
	const start = Date.now();

	while (Date.now() - start < maxSeconds * 1000) {
		const response = await fetch(tokenUrl, {
			method: 'POST',
			headers: { 'X-Requested-With': 'XMLHttpRequest' },
			body: formData,
		});

		await debugLogResponse(
			'/module/devices/bpca/oauth/device/token',
			response
		);

		if (response.status === 200) return response;

		if (response.status === 403 || response.status === 400) {
			await new Promise(r => setTimeout(r, intervalSeconds * 1000));
			continue;
		}

		if (response.status === 429) {
			await new Promise(r => setTimeout(r, 10000));
			continue;
		}

		return response;
	}

	throw new Error(
		'[Migrate] Token polling timed out after ' + maxSeconds + ' s'
	);
}

function normalizeOrientation(v: string | null) {
	if (!v) return 'left';
	const n = Number(v);
	if (!isNaN(n)) {
		if (n === 0) return 'default';
		if (n === 90) return 'right';
		if (n === 180) return 'down';
		if (n === 270) return 'left';
	}
	if (v === 'default' || v === 'right' || v === 'down' || v === 'left')
		return v;
	return 'left';
}

export default function Migrate() {
	const { dispatch } = useContext(AppStateContext);
	const { t } = useTranslation();
	const hasRun = useRef(false);

	const params = new URLSearchParams(window.location.search);
	const account: Record<string, string | null> = {};

	if (params.get('apk') === 'true') {
		params
			.get('account')
			?.split('\n')
			.forEach(line => {
				line = line.trim();
				if (!line || line.startsWith(';')) return;
				const [key, value] = line.includes('=')
					? line.split('=')
					: [line, null];
				account[key] = value?.trim() || null;
			});
	}

	const oldUrl = account.url;
	const endingMatch = oldUrl?.match(/(?<=-)[A-Za-z]{2}/)?.[0];
	const brandMatch = oldUrl?.match(/(?<=\.)[A-Za-z]+(?=-)/)?.[0];
	const kvpsMatch = oldUrl?.match(/([A-Z]{3}\d{5})/i)?.[0];

	const market = data.brands
		.find(brand => brand.name === brandMatch)
		?.markets.find(market => market.ending === endingMatch);

	account?.language && localStorage.setItem('language', account?.language);
	if (account.url?.includes('qs')) {
		localStorage.setItem('use_qs', 'true');
		localStorage.setItem('qs_url', account.url);
	} else {
		localStorage.setItem('use_qs', 'false');
		market?.urls.delivery &&
			localStorage.setItem('url_delivery', market?.urls.delivery);
		market?.urls.pairing &&
			localStorage.setItem('url_pairing', market?.urls.pairing);
		market?.urls.vitals &&
			localStorage.setItem('url_vitals', market?.urls.vitals);
	}
	useEffect(() => {
		if (hasRun.current) return;
		hasRun.current = true;

		(async () => {
			if (!market?.urls?.pairing) return;
			if (!account.id) return;

			const hardware =
				account.umdasch_version &&
				account.umdasch_version !== 'undefined'
					? account.umdasch_version
					: 'v1';
			localStorage.setItem('hardware', hardware);

			const orientation = normalizeOrientation(
				account.secondscreen_rotation
			);
			localStorage.setItem('orientation', orientation);
			localStorage.setItem('show-second-screen', 'true');

			const codes = await requestDeviceCode(
				market.urls.pairing + '/module/devices/bpca'
			);
			const { device_code, user_code, interval = 5 } = codes;

			await migrateDevice({
				userCode: user_code,
				newUrl: market.urls?.pairing || '',
				oldUrl: oldUrl || '',
			});

			const tokenUrl = `${market.urls.pairing}/module/devices/bpca/oauth/device/token`;
			const response = await pollForToken(
				tokenUrl,
				device_code,
				interval,
				90
			);
			if (response.status !== 200) return;

			const activationResponse: ExchangeDeviceCodeResponse =
				await response.json();

			localStorage.setItem('pairing', 'true');
			localStorage.setItem('id_token', activationResponse.id_token);
			localStorage.setItem(
				'refresh_token',
				activationResponse.refresh_token
			);
			localStorage.setItem('device_code', device_code);
			brandMatch && localStorage.setItem('brand', brandMatch);
			localStorage.setItem('market', market.name);
			localStorage.setItem('SetupVersion', packageJson.version);

			dispatch({ type: 'SET_BRAND', value: brandMatch ?? null });
			localStorage.setItem('brand', brandMatch ?? '');
			dispatch({ type: 'SET_KVPS', value: kvpsMatch ?? null });
			dispatch({ type: 'SET_MARKET', value: market });
			dispatch({
				type: 'SET_DEVICE',
				value: activationResponse.id_token,
			});
			dispatch({ type: 'SET_PINPAIRING_DEVICECODE', value: device_code });
			dispatch({
				type: 'SET_PINPAIRING_ACTIVATIONRESPONSE',
				value: activationResponse,
			});
			dispatch({ type: 'SET_GO_TO_LABEL', value: false });
			dispatch({
				type: 'SET_MARKET',
				value: {
					urls: market.urls,
				},
			});
			// @ts-ignore
			dispatch({ type: 'SET_LANGUAGE', value: account.language });
			dispatch({ type: 'SET_SCREEN', value: ScreenEnum.final });
		})();
	}, []);

	return <div>{t('RunningMigration')}</div>;
}
